/**
 * features/media/useSiteImages.ts
 * Live subscription to the `siteContent/images` document.
 *
 * Used by BOTH the admin media library and the public company page, so
 * an upload appears on the live site without a rebuild or a refresh.
 * The document is world-readable (see firestore.rules) because the
 * landing page runs unauthenticated.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS, SITE_IMAGES_DOC_ID, type SiteImageMap } from '@/types';

export function useSiteImages() {
  const [images, setImages] = useState<SiteImageMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ref = doc(db, COLLECTIONS.siteContent, SITE_IMAGES_DOC_ID);
    return onSnapshot(
      ref,
      (snap) => {
        setImages(snap.exists() ? (snap.data() as SiteImageMap) : {});
        setLoading(false);
      },
      (err) => {
        // The public page must still render its fallbacks if this read
        // fails (offline, rules not yet deployed) — never block on it.
        console.warn('[siteImages] subscription failed', err);
        setLoading(false);
      },
    );
  }, []);

  return { images, loading };
}
